/**
 * useSessionExpiry
 * 
 * Tracks when the first file was uploaded in a session and blocks
 * submission if the session is older than the expiry window.
 * 
 * This prevents submitting metadata for files that have already been
 * cleaned up by the Dropbox _incoming crawler (24hr sweep).
 * 
 * Uses 22 hours by default to give a safety buffer before the 24hr cleanup.
 */

import { useState, useCallback, useRef, useEffect } from "react";

const DEFAULT_EXPIRY_MS = 22 * 60 * 60 * 1000; // 22 hours

export function useSessionExpiry(expiryMs = DEFAULT_EXPIRY_MS) {
  const [firstUploadAt, setFirstUploadAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const timerRef = useRef(null);

  // Call this when a file finishes uploading — only records the first timestamp
  const markFileUploaded = useCallback(() => {
    setFirstUploadAt((prev) => prev ?? Date.now());
  }, []);

  // Check if the session has expired
  const isExpired = useCallback(() => {
    if (!firstUploadAt) return false;
    return Date.now() - firstUploadAt >= expiryMs;
  }, [firstUploadAt, expiryMs]);

  // Call this on form reset / successful submit
  const resetExpiry = useCallback(() => {
    setFirstUploadAt(null);
    setTimeRemaining(null);
  }, []);

  // Tick the remaining time once uploads have started
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (!firstUploadAt) {
      setTimeRemaining(null);
      return;
    }

    const tick = () => {
      const remaining = expiryMs - (Date.now() - firstUploadAt);
      setTimeRemaining(Math.max(0, remaining));
    };

    tick();
    timerRef.current = setInterval(tick, 60_000); // update every minute

    return () => clearInterval(timerRef.current);
  }, [firstUploadAt, expiryMs]);

  // Format remaining time for display
  const formatRemaining = useCallback(() => {
    if (timeRemaining === null) return null;
    if (timeRemaining <= 0) return "Expired";

    const hours = Math.floor(timeRemaining / (60 * 60 * 1000));
    const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / 60_000);

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [timeRemaining]);

  // Severity level for UI styling
  const urgency = (() => {
    if (timeRemaining === null) return null;
    if (timeRemaining <= 0) return "expired";
    if (timeRemaining <= 2 * 60 * 60 * 1000) return "critical";  // < 2 hours
    if (timeRemaining <= 6 * 60 * 60 * 1000) return "warning";   // < 6 hours
    return "ok";
  })();

  return {
    firstUploadAt,
    timeRemaining,
    urgency,
    markFileUploaded,
    isExpired,
    resetExpiry,
    formatRemaining,
  };
}
